import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Row, Col, Form, Table, Alert, Badge, 
  Modal, DropdownButton, Dropdown, ButtonGroup 
} from 'react-bootstrap';
import { 
  getFirestore, collection, addDoc, query, orderBy, 
  getDocs, Timestamp, doc, updateDoc, deleteDoc 
} from 'firebase/firestore';
import { 
  FaDownload, FaSearch, FaCalendar, FaEdit, FaTrash, 
  FaEllipsisV, FaPlus, FaFilter 
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from 'xlsx';
import './AdminEnquiry.css';

const AdminEnquiry = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalEnquiries, setTotalEnquiries] = useState(0);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [courseFilter, setCourseFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  
  const courseOptions = [
    'ADCA', 'DCA', 'CCC', 'O-Level', 'A-Level', 
    'Tally', 'Web Design', 'Graphic Design', 'Programming', 'DTP',
    'English' // Added English based on your data
  ];

  const statusOptions = ['All', 'New', 'Contacted', 'Registered', 'Rejected', 'Pending'];

  const formDataInitialState = {
    name: '',
    email: '',
    phone: '',
    course: '',
    message: '',
    status: 'New'
  };

  const [formData, setFormData] = useState(formDataInitialState);

  const db = getFirestore();

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [enquiries, statusFilter, courseFilter, searchTerm, startDate, endDate]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const enquiriesRef = collection(db, 'enquiries');
      const q = query(enquiriesRef, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      let fetchedEnquiries = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Include ALL enquiries regardless of whether they have teacherId or not
        fetchedEnquiries.push({ 
          id: doc.id, 
          ...data,
          // Handle different timestamp field names
          timestamp: data.createdAt || data.timestamp || Timestamp.now()
        });
      });
      
      setEnquiries(fetchedEnquiries);
      setTotalEnquiries(fetchedEnquiries.length);
    } catch (error) {
      setError('Error fetching enquiries: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];
    
    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(enquiry => enquiry.status === statusFilter);
    }
    
    // Apply course filter
    if (courseFilter !== 'All') {
      filtered = filtered.filter(enquiry => enquiry.course === courseFilter);
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(enquiry => 
        enquiry.name.toLowerCase().includes(term) ||
        enquiry.email.toLowerCase().includes(term) ||
        enquiry.phone.includes(term) ||
        enquiry.course.toLowerCase().includes(term)
      );
    }
    
    // Apply date filter
    if (startDate && endDate) {
      filtered = filtered.filter(enquiry => {
        const enquiryDate = enquiry.timestamp.toDate();
        return enquiryDate >= startDate && enquiryDate <= endDate;
      });
    }
    
    setFilteredEnquiries(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEnquiry) {
        // Update existing enquiry
        const docRef = doc(db, 'enquiries', editingEnquiry.id);
        await updateDoc(docRef, {
          ...formData,
          lastUpdated: Timestamp.now()
        });
        setSuccess('Enquiry updated successfully!');
      } else {
        // Add new enquiry
        await addDoc(collection(db, 'enquiries'), {
          ...formData,
          createdAt: Timestamp.now(),
          status: 'New'
        });
        setSuccess('Enquiry submitted successfully!');
      }
      
      setFormData(formDataInitialState);
      setEditingEnquiry(null);
      setShowModal(false);
      fetchEnquiries();
    } catch (error) {
      setError('Error submitting enquiry: ' + error.message);
    }
  };

  const handleEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setFormData({
      name: enquiry.name,
      email: enquiry.email,
      phone: enquiry.phone,
      course: enquiry.course,
      message: enquiry.message,
      status: enquiry.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      try {
        await deleteDoc(doc(db, 'enquiries', id));
        setSuccess('Enquiry deleted successfully!');
        fetchEnquiries();
      } catch (error) {
        setError('Error deleting enquiry: ' + error.message);
      }
    }
  };

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStatusFilter('All');
    setCourseFilter('All');
    setSearchTerm('');
  };

  const handleDownload = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredEnquiries.map(e => ({
        Name: e.name,
        Email: e.email,
        Phone: e.phone,
        Course: e.course,
        Message: e.message,
        Status: e.status,
        Date: e.timestamp.toDate().toLocaleDateString(),
        Time: e.timestamp.toDate().toLocaleTimeString(),
        Source: e.teacherId ? 'Teacher' : 'Admin'
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Enquiries');
    XLSX.writeFile(wb, `Enquiries_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
  };

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      const docRef = doc(db, 'enquiries', enquiryId);
      await updateDoc(docRef, {
        status: newStatus,
        lastUpdated: Timestamp.now()
      });
      setSuccess(`Status updated to ${newStatus} successfully!`);
      fetchEnquiries();
    } catch (error) {
      setError('Error updating status: ' + error.message);
    }
  };

  return (
    <div className="admin-enquiry p-4">
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Row className="mb-4">
        <Col md={3}>
          <Card className="stat-card primary">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="stats-icon">
                  <FaPlus />
                </div>
                <div className="ms-3">
                  <h4>{totalEnquiries}</h4>
                  <p>Total Enquiries</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card success">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="stats-icon">
                  <FaFilter />
                </div>
                <div className="ms-3">
                  <h4>{filteredEnquiries.length}</h4>
                  <p>Filtered Enquiries</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="bg-light">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Quick Actions</h5>
                <div>
                  <Button 
                    variant="primary" 
                    className="me-2"
                    onClick={() => setShowModal(true)}
                  >
                    <FaPlus className="me-1" /> Add Enquiry
                  </Button>
                  <Button variant="success" onClick={handleDownload}>
                    <FaDownload className="me-1" /> Export
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters Section */}
      <Card className="mb-4 filter-card">
        <Card.Body>
          <h5 className="mb-3"><FaFilter className="me-2" />Filters</h5>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, phone or course"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Course</Form.Label>
                <Form.Select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  <option value="All">All Courses</option>
                  {courseOptions.map(course => (
                    <option key={course} value={course}>{course}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Date Range</Form.Label>
                <div className="d-flex gap-2">
                  <DatePicker
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    placeholderText="Start Date"
                    className="form-control"
                  />
                  <DatePicker
                    selected={endDate}
                    onChange={date => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    placeholderText="End Date"
                    className="form-control"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <div className="d-flex gap-2 mb-3">
                <Button variant="outline-secondary" onClick={handleResetFilters}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Enquiries Table */}
      <Card className="enquiry-table-card">
        <Card.Body>
          <div className="table-responsive">
            <Table hover className="enquiry-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Name</th>
                  <th>Contact Info</th>
                  <th>Course</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.length > 0 ? (
                  filteredEnquiries.map((enquiry) => (
                    <tr key={enquiry.id} className="enquiry-row">
                      <td>
                        <div className="fw-bold">{enquiry.timestamp.toDate().toLocaleDateString()}</div>
                        <div className="small text-muted">{enquiry.timestamp.toDate().toLocaleTimeString()}</div>
                      </td>
                      <td>
                        <div className="fw-bold">{enquiry.name}</div>
                        {enquiry.message && (
                          <div className="small text-truncate" style={{maxWidth: '150px'}} title={enquiry.message}>
                            {enquiry.message}
                          </div>
                        )}
                      </td>
                      <td>
                        <div>{enquiry.email}</div>
                        <div className="small">{enquiry.phone}</div>
                      </td>
                      <td>
                        <Badge bg="light" text="dark" className="course-badge">
                          {enquiry.course}
                        </Badge>
                      </td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant={
                              enquiry.status === 'New' || enquiry.status === 'Pending' ? 'primary' :
                              enquiry.status === 'Contacted' ? 'warning' :
                              enquiry.status === 'Registered' ? 'success' : 'danger'
                            }
                            size="sm"
                            style={{ width: '120px' }}
                          >
                            {enquiry.status}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item 
                              onClick={() => handleStatusChange(enquiry.id, 'New')}
                              active={enquiry.status === 'New'}
                            >
                              New
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => handleStatusChange(enquiry.id, 'Contacted')}
                              active={enquiry.status === 'Contacted'}
                            >
                              Contacted
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => handleStatusChange(enquiry.id, 'Registered')}
                              active={enquiry.status === 'Registered'}
                            >
                              Registered
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => handleStatusChange(enquiry.id, 'Rejected')}
                              active={enquiry.status === 'Rejected'}
                            >
                              Rejected
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                      <td>
                        <Badge bg={enquiry.teacherId ? "info" : "secondary"}>
                          {enquiry.teacherId ? "Teacher" : "Admin"}
                        </Badge>
                      </td>
                      <td>
                        <Dropdown as={ButtonGroup} size="sm">
                          <Dropdown.Toggle variant="outline-secondary" id="dropdown-basic">
                            <FaEllipsisV />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleEdit(enquiry)}>
                              <FaEdit className="me-2" /> Edit
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleDelete(enquiry.id)} className="text-danger">
                              <FaTrash className="me-2" /> Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      {loading ? 'Loading enquiries...' : 'No enquiries found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        setEditingEnquiry(null);
        setFormData(formDataInitialState);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingEnquiry ? 'Edit Enquiry' : 'Add New Enquiry'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Course *</Form.Label>
                  <Form.Select
                    value={formData.course}
                    onChange={(e) => setFormData({...formData, course: e.target.value})}
                    required
                  >
                    <option value="">Select a course</option>
                    {courseOptions.map(course => (
                      <option key={course} value={course}>{course}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
              />
            </Form.Group>
            {editingEnquiry && (
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Registered">Registered</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Pending">Pending</option>
                </Form.Select>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              setEditingEnquiry(null);
              setFormData(formDataInitialState);
            }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingEnquiry ? 'Update Enquiry' : 'Submit Enquiry'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminEnquiry;